const stringify = require("safe-stable-stringify");
/**
 * Implementation of the type PluginMethodCacheConfig that manages the cache for a specific plugin method based on the provided caching policy.
 */
class PluginMethodCacheFacade {
    constructor(plugin, pluginMethod, cachingPolicy, cacheExpirationTimeMs) {
        this.Plugin = plugin;
        this.pluginMethod = pluginMethod;
        this.cachingPolicy = cachingPolicy;
        this.cacheExpirationTime = cachingPolicy === 'Expiration' ? cacheExpirationTimeMs || 0 : 0;
        this.cache = {};
    }
    /**
     * Helper method to stringify arguments using the package 'safe-stable-stringify'. That will ensure that circular structures and bigint values get properly stringified.
     */
    stringifyArgs(args) {
        return stringify(args);
    }
    getCachingPolicy() {
        return this.cachingPolicy;
    }
    setCachingPolicy(newCachingPolicy, cacheExpirationTime) {
        if (this.cachingPolicy === newCachingPolicy) {
            return;
        }
        this.cachingPolicy = newCachingPolicy;
        if (newCachingPolicy === 'Expiration') {
            this.cacheExpirationTime = cacheExpirationTime || 0;
        }
    }
    /**
     * Internal function that invalidates the cache based on the argumnents of the method.
     * If the cache type is 'Expiration' and it is expired, the cache is invalidated as a whole, meaning that all cached values will be cleared regardless of the arguments.
     * @param args the arguments to the method
     * @returns true if the cache is available and valid for the given arguments, false otherwise.
     */
    isCacheAvailable(args) {
        if (this.cachingPolicy === 'Never') {
            return false;
        }
        const cache = this.cache[args];
        if (!cache) {
            return false;
        }
        if (this.cachingPolicy === 'Expiration') {
            const now = Date.now();
            const isAvailableByExpiration = (now - cache.cacheTimestamp) < this.cacheExpirationTime;
            if (!isAvailableByExpiration) {
                delete this.cache[args]; // remove it from the cache named object.
            }
            // if it is available by expiration, check if the cache exists for the given arguments.
            return isAvailableByExpiration;
        }
        // For 'Indefinite', the cache is valid if there is a cached value. If it reached here, it means that the cache exists for the given arguments.
        return true;
    }
    invalidateCache() {
        this.cache = {};
    }
    call(...args) {
        const stringifiedArgs = this.stringifyArgs(args);
        if (this.isCacheAvailable(stringifiedArgs)) {
            return Promise.resolve(this.cache[stringifiedArgs].value);
        }
        const resultPromise = Promise.resolve(this.Plugin[this.pluginMethod](...args));
        if (this.cachingPolicy === 'Indefinite' || this.cachingPolicy === 'Expiration') {
            resultPromise.then(result => {
                // check for undefined values that should not be cached. This is useful for methods that return void. Not testing for null here because that is a valid return value.
                if (result !== undefined) {
                    this.cache[stringifiedArgs] = {
                        value: result,
                        cacheTimestamp: Date.now()
                    };
                }
            });
        }
        return resultPromise;
    }
}
/**
 * Creates a wrapper for the plugin that applies the Deduplication strategy to the specified method.
 *
 * The Deduplication strategy checks if there is an ongoing call to the plugin method with the same arguments. If there is, it subscribes to it and returns the same promise
 * instead of calling the plugin method again. Once the ongoing call is completed, all subscribers will be resolved or rejected with the same result.
 */
class DeduplicationPluginStrategy {
    constructor(pluginMethod) {
        this.pluginMethod = pluginMethod;
        this.promisesByArgs = [];
    }
    execute(plugin, ...args) {
        // check if there is an ongoing call to the plugin method with the same arguments.
        const stringifiedArgs = stringify(args);
        const ongoingCall = this.promisesByArgs.find((call) => call.stringifiedArgs === stringifiedArgs);
        if (ongoingCall) {
            // There is an ongoing call with the same arguments, subscribe to it and return the promise.
            const promise = new Promise((resolve, reject) => {
                ongoingCall.subscriptions.push({ resolve, reject });
            });
            // Return the promise representing the subscription to the ongoing call.
            return promise;
        }
        // Otherwise, there is no ongoing call to the plugin method. Create a new call entry for this set of arguments.
        const callEntry = {
            id: Date.now(),
            args,
            stringifiedArgs,
            promise: Promise.resolve(plugin[this.pluginMethod](...args)),
            subscriptions: []
        };
        // Add it to the array of ongoing calls.
        this.promisesByArgs.push(callEntry);
        // Once the plugin method call is completed, resolve or reject all subscriptions accordingly and remove the call entry from the array of ongoing calls.
        callEntry.promise.then((result) => {
            for (const subscription of callEntry.subscriptions) {
                subscription.resolve(result);
            }
        }).catch((reason) => {
            for (const subscription of callEntry.subscriptions) {
                subscription.reject(reason);
            }
        }).finally(() => {
            this.promisesByArgs = this.promisesByArgs.filter((call) => call.id !== callEntry.id);
        });
        // Return the promise of the current call.
        return callEntry.promise;
    }
}
/**
 * Creates a wrapper for the plugin that applies caching to the specified methods based on the provided caching policies.
 *
 * @param plugin the plugin to wrap with the caching facade
 * @param cacheStrategies the caching strategies to apply to the plugin methods. If a method is not specified in this object, it will not have caching applied and will be executed normally.
 * @param cacheExpirationMs the optional expiration time for the cache in milliseconds. Defaults to zero (no expiration at all). ""TODO"": apply expiration option to 'Expiration' methods separately.
 * @returns an object containing the proxied plugin and the extended configuration for the cache facade. The extended configuration can be used to manage the cache (e.g. invalidate cache, change caching policy and others).
 */
export function applyPluginCachingFacade(plugin, cacheStrategies, cacheExpirationMs) {
    const pluginCacheExtendedConfig = {};
    for (const method in cacheStrategies) {
        const methodName = method;
        const cachingPolicy = cacheStrategies[methodName];
        pluginCacheExtendedConfig[methodName] = new PluginMethodCacheFacade(plugin, methodName, cachingPolicy, cacheExpirationMs);
    }
    const handler = {
        get(target, prop, receiver) {
            if (prop in target) {
                if (typeof prop !== 'symbol' && typeof target[prop] !== 'function') {
                    // if the properties is not a function, just return it without proxying
                    return Reflect.get(target, prop, receiver);
                }
                return (...args) => {
                    const methodName = prop;
                    // the property is a function. Check if there is a caching strategy for it, if not just execute the function normally
                    if (!pluginCacheExtendedConfig[methodName]) {
                        return Reflect.apply(target[methodName], target, args);
                    }
                    return pluginCacheExtendedConfig[methodName].call(...args);
                };
            }
            throw new Error(`Method ${String(prop)} does not exist on plugin`);
        },
        set(target, prop, value, receiver) {
            // Allow setting properties on the plugin without interference.
            return Reflect.set(target, prop, value, receiver);
        }
    };
    const proxyPlugin = new Proxy(plugin, handler);
    return {
        plugin: proxyPlugin,
        pluginExtendedConfig: pluginCacheExtendedConfig,
    };
}
/**
 * Creates a wrapper for the plugin that applies the specified strategy to the specified methods.
 *
 * @param plugin the plugin to wrap with the strategy facade
 * @param strategies what strategy to apply to each plugin method. Currently supported strategies:
 * - **Deduplication**: If there is an ongoing call to the plugin method with the same arguments, subscribe to it and return the ongoing call's promise instead of calling the plugin method again. Once the ongoing call is completed, all subscribers will be resolved or rejected with its result.
 * - **None**: the plugin method will be called normally without applying any strategy.
 *
 * **Note: If the method is not specified, it will behave just as 'None'**.
*/
export function applyPluginStrategyFacade(plugin, strategies) {
    if (!strategies || Object.keys(strategies).length === 0 || Object.values(strategies).every((strategy) => strategy === 'Default')) {
        return plugin;
    }
    let pluginFunctionStrategies = {};
    Object.keys(strategies).forEach((method) => {
        const methodName = method;
        const strategy = strategies[methodName];
        switch (strategy) {
            case 'Deduplication':
                pluginFunctionStrategies[methodName] = new DeduplicationPluginStrategy(methodName);
                break;
            default:
                break;
        }
    });
    const handler = {
        get(target, prop, receiver) {
            if (prop in target) {
                if (typeof prop !== 'symbol' && typeof target[prop] !== 'function') {
                    // if the properties is not a funtion, just return it without proxying
                    return Reflect.get(target, prop, receiver);
                }
                return (...args) => {
                    const methodName = prop;
                    // the property is a function. Check if there is a strategy for it, if not just execute the function normally
                    const strategy = pluginFunctionStrategies[methodName];
                    if (!strategy) {
                        return Reflect.apply(target[methodName], target, args);
                    }
                    return strategy.execute(target, ...args);
                };
            }
            throw new Error(`Method ${String(prop)} does not exist on plugin`);
        },
        set(target, prop, value, receiver) {
            // Allow setting properties on the plugin without interference.
            return Reflect.set(target, prop, value, receiver);
        }
    };
    return new Proxy(plugin, handler);
}
//# sourceMappingURL=plugin-strategies.js.map